// The Quote Hub (Session AQW, Phase 2 record page: Q-1/Q-2/Q-4/Q-5/Q-12). Before this a quote had
// no office page of its own -- "the one place it 'opens' is the customer accept page" -- and the
// list row carried up to ten differently-coloured action buttons at once. This page is that record:
// summary, tiers and price, lifts, versions, validity, opened/sent, Signable state and lost reason
// all in one place, with every quote action living here instead of on the list row.
//
// Every action below calls the SAME api.quotes.* methods and follows the SAME confirmation pattern
// QuotesPage.tsx already used (two-step ConfirmButton for Send/Resend/Void, a full modal for Mark
// lost/Delete, no confirmation for Duplicate/Reopen/Extend -- all reversible or additive). Nothing
// about how an action WORKS changes; only where it lives.
//
// Session ARP: which controls the page offers is no longer decided here. Every one is gated on
// quoteActions() in src/lib/quoteActions.ts, the rules the Quotes list row reads too, which closed the
// five gaps the two pages had drifted into (Edit and Extend on a sent quote, Re-quote on an expired one,
// the forced delete of an accepted quote, and a Send / Resend confirm that names its recipient).
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, ApiError } from "../../lib/api";
import type { QuoteDetail } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { quoteStatusLabel, quoteStatusTone } from "../../lib/quoteStatus";
import {
  NO_RECIPIENT_HINT,
  QUOTE_FORCED_DELETE_MESSAGE,
  QUOTE_FORCED_DELETE_WARNING,
  quoteActions,
  quoteDeleteIsForced,
  quoteResumeState,
  quoteSendConfirmLabel,
} from "../../lib/quoteActions";
import { useSingleTimeout } from "../../lib/useSingleTimeout";
import { formatDate, formatDateTime } from "../../lib/format";
import { RecordHeader } from "../../components/RecordHeader";
import { AccountSymbol } from "../../components/AccountSymbol";
import { RecordLink, RecordLinkGroup } from "../../components/RecordLink";
import { StatusPill } from "../../components/StatusPill";
import { Button } from "../../components/Button";
import { ConfirmButton } from "../../components/ConfirmButton";
import { ConfirmDeleteModal, DELETE_CONFIRM_MESSAGE } from "../../components/ConfirmDeleteModal";
import { EmptyState } from "../../components/EmptyState";
import { Loader } from "../../components/Loader";
import { FrequencyConfirmModal } from "./FrequencyConfirmModal";
import { MarkQuoteLostModal, ManageLostReasonsModal } from "./LostReasonModals";
import { QuoteVersionsModal } from "./QuoteVersionsModal";
import { QuotePriceOverridePanel } from "./QuotePriceOverridePanel";
import { QuoteOfficePanel } from "./QuoteOfficePanel";
import { QuoteSignatoryPanel } from "./QuoteSignatoryPanel";
import { voidOutcome } from "../../lib/quoteVoidMessage";

const TIER_LABEL: Record<string, string> = { core: "Core", care: "Care", complete: "Complete" };
const TIER_ORDER = ["core", "care", "complete"] as const;

function gbp(n: number | null | undefined): string {
  if (n == null) return "-";
  return `£${Math.round(n).toLocaleString("en-GB")}`;
}
function acceptUrl(token: string): string {
  return `${window.location.origin}/quote/${token}`;
}

// Parsed straight out of the stored payload, the same way the PDF route and the calculator's own
// Resume flow already do (these are payload JSON keys, not columns -- the locked QUOTE DUPLICATE
// RULE names exactly this set as acceptance-time state).
interface QuotePayloadTier {
  annual?: number;
}
interface QuotePayloadLift {
  liftType?: string;
  frequency?: string;
  stops?: number;
}
interface QuotePayloadShape {
  frequency?: string;
  numLifts?: number;
  lifts?: QuotePayloadLift[];
  tiers?: Partial<Record<"core" | "care" | "complete", QuotePayloadTier>>;
  multiDiscount?: number;
  discountLiftCount?: number;
  discountBasis?: "quote" | "portfolio";
  acceptedAt?: string;
  acceptedTier?: string;
  contractId?: string;
}
function payloadOf(q: QuoteDetail): QuotePayloadShape {
  return (q.payload ?? {}) as QuotePayloadShape;
}

const LIFT_TYPE_LABEL: Record<string, string> = {
  passengerTraction: "Passenger traction",
  passengerTractionMrl: "Passenger traction MRL",
  passengerHydr: "Passenger hydraulic",
  goodsTraction: "Goods traction",
  goodsTractionMrl: "Goods traction MRL",
  goodsHydr: "Goods hydraulic",
  platform: "Platform lift",
  service: "Service lift",
  dumbwaiter: "Dumbwaiter",
  stairlightStraight: "Stairlift (straight)",
  stairliftStraight: "Stairlift (straight)",
  stairliftCurved: "Stairlift (curved)",
  scissorKitchen: "Scissor lift (kitchen)",
  scissorGoods: "Scissor lift (goods)",
  step: "Step lift",
  throughFloor: "Through-floor lift",
  trolley: "Trolley lift",
};
function liftTypeLabel(key: string | undefined): string {
  if (!key) return "Lift";
  return LIFT_TYPE_LABEL[key] ?? key;
}

type BannerState = { kind: "ok" | "error"; message: string } | null;

export function QuoteHubPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [quote, setQuote] = useState<QuoteDetail | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error" | "not_found">("loading");
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState<BannerState>(null);

  const [showVersions, setShowVersions] = useState(false);
  const [lostOpen, setLostOpen] = useState(false);
  const [manageReasonsOpen, setManageReasonsOpen] = useState(false);
  const [freqReviewOpen, setFreqReviewOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [narrativeBusy, setNarrativeBusy] = useState(false);
  const scheduleCopyReset = useSingleTimeout();

  const load = useCallback(async () => {
    if (!id) return;
    setState("loading");
    try {
      const q = await api.quotes.get(id);
      setQuote(q);
      setState("ready");
    } catch (err) {
      setState(err instanceof ApiError && err.status === 404 ? "not_found" : "error");
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  function ok(message: string) {
    setBanner({ kind: "ok", message });
  }
  function fail(message: string) {
    setBanner({ kind: "error", message });
  }

  // Edit (and Re-quote, which is the same flow) fetches the quote through api.quotes.get before opening
  // the calculator, exactly as the list row does, and quoteResumeState builds the router state both pages
  // hand over. Session ARP recorded that this fetch, and the calculator's own re-fetch on mount, could
  // both be answered by the request cache (Session AQT) for up to five minutes, so a change made elsewhere
  // in that window (another office screen, the Connected AI) never reached the calculator, and a save
  // wrote the older figures back over it. Session ARU closed it: both reads now bypass the cache
  // (noCache), here, on the list row, and in the calculator. The page's own display above still reads
  // through the cache, as every office page does; it is only the copy the calculator edits that must be live.
  async function editQuote() {
    if (!quote) return;
    setBusy(true);
    try {
      const q = await api.quotes.get(quote.id, { noCache: true });
      navigate("/office/quotes/new", { state: quoteResumeState(q) });
    } catch {
      fail("Could not open that quote. Please try again.");
      setBusy(false);
    }
  }

  async function sendDraft() {
    if (!quote) return;
    setBusy(true);
    try {
      const res = await api.quotes.send(quote.id);
      await load();
      if (res.emailed) ok(`Sent to ${res.recipient ?? "the customer"}.`);
      else fail("Marked as sent but the email did not go out. Check that Gmail is connected, then resend it.");
    } catch (err) {
      fail(friendlyApiError(err, "Could not send this quote. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  async function resendQuote() {
    if (!quote) return;
    setBusy(true);
    try {
      const res = await api.quotes.resend(quote.id);
      await load();
      if (!res.emailed) {
        fail("Updated but the email did not go out. Check that Gmail is connected, then resend it.");
        return;
      }
      ok("Resent to the customer.");
    } catch (err) {
      fail(friendlyApiError(err, "Could not resend this quote. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  async function voidQuote() {
    if (!quote) return;
    setBusy(true);
    try {
      const res = await api.quotes.voidEnvelope(quote.id);
      await load();
      const outcome = voidOutcome(`Quote ${quote.quoteRef}`, res);
      if (outcome.kind === "error") fail(outcome.text);
      else ok(outcome.text);
    } catch (err) {
      fail(friendlyApiError(err, "Could not void this quote. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  async function reopenQuote() {
    if (!quote) return;
    setBusy(true);
    try {
      await api.quotes.reopen(quote.id);
      await load();
      ok("Reopened as a draft.");
    } catch (err) {
      fail(friendlyApiError(err, "Could not reopen this quote. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  async function duplicateQuote() {
    if (!quote) return;
    setBusy(true);
    try {
      const dup = await api.quotes.duplicate(quote.id);
      navigate(`/office/quotes/${dup.id}`);
    } catch (err) {
      fail(friendlyApiError(err, "Could not duplicate this quote. Please try again."));
      setBusy(false);
    }
  }

  async function extendValidity() {
    if (!quote) return;
    setBusy(true);
    try {
      await api.quotes.extendValidity(quote.id);
      await load();
      ok("Validity extended.");
    } catch (err) {
      fail(friendlyApiError(err, "Could not extend validity. Please try again."));
    } finally {
      setBusy(false);
    }
  }

  async function copyLink() {
    if (!quote?.token) return;
    try {
      await navigator.clipboard.writeText(acceptUrl(quote.token));
      setCopied(true);
      scheduleCopyReset(() => setCopied(false), 2000);
    } catch {
      fail("Could not copy the link. Copy it from the address shown below instead.");
    }
  }

  async function regenerateNarrative() {
    if (!quote) return;
    setNarrativeBusy(true);
    try {
      const res = await api.quotes.regenerateNarrative(quote.id);
      setQuote((q) => (q ? { ...q, quoteNarrative: res.quoteNarrative } : q));
      if (!res.regenerated) fail("Could not regenerate the narrative right now. The existing text is unchanged.");
      else ok("Narrative regenerated.");
    } catch (err) {
      fail(friendlyApiError(err, "Could not regenerate the narrative. Please try again."));
    } finally {
      setNarrativeBusy(false);
    }
  }

  async function confirmDelete() {
    if (!quote) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      // Forced for an accepted quote as well as a signed one: both have rendered a contract, and the
      // server refuses an unforced delete of either (409 has_contract).
      await api.quotes.delete(quote.id, quoteDeleteIsForced(quote.status));
      navigate("/office/quotes");
    } catch (err) {
      setDeleteError(friendlyApiError(err, "Could not delete this quote. Please try again."));
    } finally {
      setDeleting(false);
    }
  }

  if (state === "loading") {
    return (
      <div className="p-4 sm:p-6">
        <Loader shape="panel" />
      </div>
    );
  }

  if (state === "not_found") {
    return (
      <div className="p-4 sm:p-6">
        <EmptyState
          message="That quote could not be found."
          action={
            <Link to="/office/quotes" className="text-sm text-ele-blue underline">
              Back to Quotes
            </Link>
          }
        />
      </div>
    );
  }

  if (state === "error" || !quote) {
    return (
      <div className="p-4 sm:p-6">
        <EmptyState
          message="Could not load this quote."
          action={
            <button type="button" onClick={() => void load()} className="text-sm text-ele-blue underline">
              Retry
            </button>
          }
        />
      </div>
    );
  }

  const p = payloadOf(quote);
  const lifts = Array.isArray(p.lifts) ? p.lifts : [];
  const recommendedTier = quote.recommendedTier ?? "core";
  const primaryBtn =
    "inline-flex items-center justify-center gap-1.5 rounded-ele bg-ele-green px-4 py-2 font-heading text-sm tracking-wide text-ele-bg shadow-sm transition hover:brightness-110 disabled:opacity-50";
  const outlineBtn =
    "inline-flex items-center justify-center gap-1.5 rounded-ele border border-ele-danger px-4 py-2 font-heading text-sm tracking-wide text-ele-danger transition hover:bg-ele-danger/10 disabled:opacity-50";
  // The ONE set of actions this quote offers, shared with the Quotes list row (Session ARP).
  const actions = quoteActions(quote, { isAdmin });
  const deleteForced = quoteDeleteIsForced(quote.status);

  return (
    <div className="p-4 sm:p-6">
      <RecordHeader
        title={quote.quoteRef}
        backTo="/office/quotes"
        backLabel="Back to Quotes"
        status={<StatusPill word={quoteStatusLabel(quote.status)} tone={quoteStatusTone(quote.status)} />}
        subtitle={
          <>
            {quote.customer ? (
              <Link to={`/office/customers/${quote.customer.id}`} className="text-ele-purple hover:underline">
                {quote.customer.name}
              </Link>
            ) : (
              (quote.customerName ?? "No customer linked")
            )}
            {quote.customer && <AccountSymbol customerId={quote.customer.id} />}
            {quote.siteName ? ` · ${quote.siteName}` : ""}
          </>
        }
        actions={
          <>
            {/* Send and Resend name their recipient when armed (the AEH rule). With no address to go
                to they carry the hint on hover but stay LIVE, as on the list row: a disabled button
                paints no title and leaves the tab order, so the office would get a greyed button and
                no reason, where pressing it returns an alert naming both places to fix. */}
            {actions.includes("send") && (
              <ConfirmButton
                onConfirm={sendDraft}
                disabled={busy}
                busy={busy}
                label="Send"
                busyLabel="Sending..."
                confirmLabel={quoteSendConfirmLabel("Send", quote)}
                title={quote.recipientEmail === null ? NO_RECIPIENT_HINT : undefined}
                className={primaryBtn}
              />
            )}
            {actions.includes("resend") && (
              <ConfirmButton
                onConfirm={resendQuote}
                disabled={busy}
                busy={busy}
                label="Resend"
                busyLabel="Resending..."
                confirmLabel={quoteSendConfirmLabel("Resend", quote)}
                title={quote.recipientEmail === null ? NO_RECIPIENT_HINT : undefined}
                className={primaryBtn}
              />
            )}
            {actions.includes("edit") && (
              <Button variant="secondary" size="sm" onClick={editQuote} disabled={busy}>
                Edit
              </Button>
            )}
            {actions.includes("requote") && (
              <Button variant="secondary" size="sm" onClick={editQuote} disabled={busy}>
                Re-quote
              </Button>
            )}
            {actions.includes("viewPdf") && (
              <a
                href={`/api/quotes/${quote.id}/pdf`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-1.5 rounded-ele border border-ele-green px-4 py-2 font-heading text-sm tracking-wide text-ele-green transition hover:bg-ele-green hover:text-ele-bg"
              >
                View PDF
              </a>
            )}
            {actions.includes("viewQuote") && quote.token && (
              <a
                href={acceptUrl(quote.token)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-1.5 rounded-ele border border-ele-blue px-4 py-2 font-heading text-sm tracking-wide text-ele-blue transition hover:bg-ele-blue hover:text-ele-bg"
              >
                View quote
              </a>
            )}
            {actions.includes("extend") && (
              <Button variant="secondary" size="sm" onClick={extendValidity} disabled={busy}>
                Extend validity
              </Button>
            )}
            {actions.includes("reopen") && (
              <Button variant="secondary" size="sm" onClick={reopenQuote} disabled={busy}>
                Reopen as draft
              </Button>
            )}
            {actions.includes("void") && (
              <ConfirmButton onConfirm={voidQuote} disabled={busy} busy={busy} label="Void" busyLabel="Voiding..." confirmLabel="Cancel the signature request?" className={outlineBtn} title="Cancels the signature request in Signable. This cannot be undone." />
            )}
            {actions.includes("markLost") && (
              <Button variant="ghost" size="sm" onClick={() => setLostOpen(true)} disabled={busy}>
                Mark lost
              </Button>
            )}
            {actions.includes("duplicate") && (
              <Button variant="ghost" size="sm" onClick={duplicateQuote} disabled={busy}>
                Duplicate
              </Button>
            )}
            {actions.includes("delete") && (
              <Button variant="destructive" size="sm" onClick={() => setDeleteOpen(true)} disabled={busy}>
                Delete
              </Button>
            )}
          </>
        }
      />

      {banner && (
        <p
          role={banner.kind === "error" ? "alert" : "status"}
          className={`mt-4 rounded-ele border px-3 py-2 text-sm ${
            banner.kind === "error" ? "border-ele-danger text-ele-danger" : "border-ele-green text-ele-green"
          }`}
        >
          {banner.message}
        </p>
      )}

      {actions.includes("reviewFrequency") && (
        <div className="mt-4 rounded-ele border border-amber-400 bg-ele-surface p-3">
          <p className="text-sm text-ele-strong">The customer accepted at a different visit frequency than quoted. This needs review before the agreement can be sent.</p>
          {quote.renewalOfContractId ? (
            <Link to={`/office/contracts/${quote.renewalOfContractId}`} className="mt-1 inline-block text-sm text-ele-blue underline">
              Review on the contract page
            </Link>
          ) : (
            <button type="button" onClick={() => setFreqReviewOpen(true)} className="mt-1 text-sm text-ele-blue underline">
              Review frequency
            </button>
          )}
        </div>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <section className="rounded-ele border border-ele-hairline bg-ele-surface p-4">
            <h2 className="font-heading text-lg tracking-wide text-ele-strong">Summary</h2>
            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 text-sm sm:grid-cols-3">
              <div>
                <dt className="text-ele-body opacity-70">Value</dt>
                <dd className="text-ele-strong">{quote.annualValue != null ? `${gbp(quote.annualValue)}/yr` : "-"}</dd>
              </div>
              <div>
                <dt className="text-ele-body opacity-70">Recommended tier</dt>
                <dd className="text-ele-strong">{TIER_LABEL[recommendedTier] ?? recommendedTier}</dd>
              </div>
              <div>
                <dt className="text-ele-body opacity-70">Lifts</dt>
                <dd className="text-ele-strong">{p.numLifts ?? lifts.length}</dd>
              </div>
              <div>
                <dt className="text-ele-body opacity-70">Frequency</dt>
                <dd className="text-ele-strong">{p.frequency ?? "-"}</dd>
              </div>
              {p.multiDiscount ? (
                <div>
                  <dt className="text-ele-body opacity-70">Multi-lift discount</dt>
                  <dd className="text-ele-strong">
                    {Math.round(p.multiDiscount * 100)}%{p.discountBasis === "portfolio" ? " (customer portfolio)" : ""}
                  </dd>
                </div>
              ) : null}
              <div>
                <dt className="text-ele-body opacity-70">Created</dt>
                <dd className="text-ele-strong">{formatDate(quote.createdAt)}</dd>
              </div>
            </dl>
          </section>

          <section className="rounded-ele border border-ele-hairline bg-ele-surface p-4">
            <h2 className="font-heading text-lg tracking-wide text-ele-strong">Tiers and price</h2>
            <div className="mt-3 grid grid-cols-3 gap-3">
              {TIER_ORDER.map((t) => (
                <div
                  key={t}
                  className={`rounded-ele border p-3 ${t === recommendedTier ? "border-ele-green" : "border-ele-hairline"}`}
                >
                  <p className="font-menu text-[10px] uppercase tracking-wide text-ele-body opacity-70">
                    {TIER_LABEL[t]}
                    {t === recommendedTier ? " · Recommended" : ""}
                  </p>
                  <p className="mt-1 font-heading text-lg text-ele-strong">{gbp(p.tiers?.[t]?.annual)}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Session ARF: correct an issued quote's price in place (renders only when the server says it can). */}
          {["pending", "sent", "opened"].includes(quote.status) && <QuotePriceOverridePanel quoteId={quote.id} onChanged={() => void load()} />}

          <RecordLinkGroup title="Lifts" count={lifts.length} empty="No lifts on this quote.">
            {lifts.map((l, i) => (
              <div key={i} className="rounded-ele border border-ele-hairline bg-ele-surface px-3 py-2 text-sm">
                <span className="text-ele-strong">{liftTypeLabel(l.liftType)}</span>
                {l.frequency ? <span className="ml-2 text-ele-body opacity-70">{l.frequency}</span> : null}
                {l.stops ? <span className="ml-2 text-ele-body opacity-70">{l.stops} stops</span> : null}
              </div>
            ))}
          </RecordLinkGroup>

          <section className="rounded-ele border border-ele-hairline bg-ele-surface p-4">
            <div className="flex items-center justify-between gap-2">
              <h2 className="font-heading text-lg tracking-wide text-ele-strong">AI narrative</h2>
              <button
                type="button"
                onClick={regenerateNarrative}
                disabled={narrativeBusy}
                className="text-xs text-ele-blue underline hover:text-ele-strong disabled:opacity-50"
              >
                {narrativeBusy ? "Regenerating..." : "Regenerate"}
              </button>
            </div>
            {quote.quoteNarrative ? (
              <p className="mt-2 whitespace-pre-wrap text-sm text-ele-body">{quote.quoteNarrative}</p>
            ) : (
              <p className="mt-2 text-sm text-ele-body opacity-70">No narrative generated yet.</p>
            )}
          </section>
        </div>

        <div className="space-y-6">
          <section className="rounded-ele border border-ele-hairline bg-ele-surface p-4">
            <h2 className="font-heading text-lg tracking-wide text-ele-strong">Status</h2>
            <dl className="mt-3 space-y-2 text-sm">
              <div className="flex justify-between gap-2">
                <dt className="text-ele-body opacity-70">Sent</dt>
                <dd className="text-ele-strong">{quote.sentAt ? formatDateTime(quote.sentAt) : "Not sent"}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-ele-body opacity-70">Opened</dt>
                <dd className="text-ele-strong">{quote.openedAt ? formatDateTime(quote.openedAt) : "Not yet"}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-ele-body opacity-70">Valid until</dt>
                <dd className="text-ele-strong">{quote.validUntil ? formatDate(quote.validUntil) : "-"}</dd>
              </div>
              {p.acceptedAt && (
                <div className="flex justify-between gap-2">
                  <dt className="text-ele-body opacity-70">Accepted</dt>
                  <dd className="text-ele-strong">
                    {formatDateTime(p.acceptedAt)}
                    {p.acceptedTier ? ` (${TIER_LABEL[p.acceptedTier] ?? p.acceptedTier})` : ""}
                  </dd>
                </div>
              )}
              {quote.signableEnvelopeId && (
                <div className="flex justify-between gap-2">
                  <dt className="text-ele-body opacity-70">Signable</dt>
                  <dd className="text-ele-strong">{quote.status === "signed" ? "Signed" : "Sent, awaiting signature"}</dd>
                </div>
              )}
              {quote.status === "lost" && (
                <div>
                  <div className="flex justify-between gap-2">
                    <dt className="text-ele-body opacity-70">Lost</dt>
                    <dd className="text-ele-strong">
                      {quote.lostAt ? formatDate(quote.lostAt) : ""}
                      {quote.lostReason ? ` · ${quote.lostReason.label}` : ""}
                    </dd>
                  </div>
                  {quote.lostNote && <p className="mt-1 text-xs text-ele-body opacity-70">{quote.lostNote}</p>}
                </div>
              )}
            </dl>
            {actions.includes("history") && (
              <button type="button" onClick={() => setShowVersions(true)} className="mt-3 text-xs text-ele-blue underline hover:text-ele-strong">
                Version history
              </button>
            )}
          </section>

          {/* Session AUJ: who signs the agreement, when it is not the quote's contact. */}
          <QuoteSignatoryPanel quoteId={quote.id} />

          {/* Session ASH (Q-10): owner, internal notes and office documents. Never shown to the customer. */}
          <QuoteOfficePanel quoteId={quote.id} />

          <RecordLinkGroup title="Related" empty="Nothing linked to this quote yet.">
            {quote.customer && <RecordLink key="customer" to={`/office/customers/${quote.customer.id}`} label={quote.customer.name} meta="Customer" />}
            {quote.site && <RecordLink key="site" to={`/office/sites/${quote.site.id}`} label={quote.site.name} meta="Site" />}
            {quote.portfolio && (
              <RecordLink key="portfolio" to={`/office/quote-portfolios/${quote.portfolio.id}`} label={quote.portfolio.name} meta="Portfolio" />
            )}
            {quote.renewalOfContractId && (
              <RecordLink key="renewal" to={`/office/contracts/${quote.renewalOfContractId}`} label="View the contract this renews" meta="Renewal" />
            )}
            {p.contractId && <RecordLink key="contract" to={`/office/contracts/${p.contractId}`} label="View the resulting contract" meta="Contract" />}
          </RecordLinkGroup>

          {quote.token && quote.status !== "draft" && (
            <section className="rounded-ele border border-ele-hairline bg-ele-surface p-4">
              <h2 className="font-heading text-lg tracking-wide text-ele-strong">Accept link</h2>
              <p className="mt-2 break-all text-xs text-ele-body">{acceptUrl(quote.token)}</p>
              {actions.includes("copyLink") && (
                <button
                  type="button"
                  onClick={copyLink}
                  className="mt-2 rounded-ele border border-ele-purple-accent px-3 py-1.5 text-xs text-ele-body hover:text-ele-strong"
                >
                  {copied ? "Copied" : "Copy link"}
                </button>
              )}
            </section>
          )}
        </div>
      </div>

      {showVersions && <QuoteVersionsModal quoteId={quote.id} quoteRef={quote.quoteRef} isAdmin={isAdmin} onClose={() => setShowVersions(false)} />}

      {lostOpen && (
        <MarkQuoteLostModal
          quoteId={quote.id}
          quoteRef={quote.quoteRef}
          onClose={() => setLostOpen(false)}
          onManageReasons={() => {
            setLostOpen(false);
            setManageReasonsOpen(true);
          }}
          onMarked={(ref) => {
            setLostOpen(false);
            ok(`Quote ${ref} marked lost.`);
            void load();
          }}
        />
      )}
      {manageReasonsOpen && <ManageLostReasonsModal onClose={() => setManageReasonsOpen(false)} />}

      {freqReviewOpen && (
        <FrequencyConfirmModal
          quoteId={quote.id}
          quoteRef={quote.quoteRef}
          onClose={() => setFreqReviewOpen(false)}
          onConfirmed={(message) => {
            setFreqReviewOpen(false);
            ok(message);
            void load();
          }}
        />
      )}

      <ConfirmDeleteModal
        open={deleteOpen}
        title={`Delete quote ${quote.quoteRef}?`}
        message={deleteForced ? QUOTE_FORCED_DELETE_MESSAGE : DELETE_CONFIRM_MESSAGE}
        warning={deleteForced ? QUOTE_FORCED_DELETE_WARNING : undefined}
        confirmLabel="Delete quote"
        busy={deleting}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={() => {
          if (deleting) return;
          setDeleteOpen(false);
          setDeleteError(null);
        }}
      />
    </div>
  );
}
